import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  Check,
  Info,
  List,
  Loader2,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { inputClass, selectClass } from "../components/FormField";
import { HospitalActsModal } from "../components/HospitalActsModal";
import {
  classifyAdminHospitalActs,
  classifyAllAdminHospitalActs,
  fetchAdminHospitalActs,
} from "../api/adminApi";
import { fetchAuditorHospitalActs } from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { getHospitalActDetails } from "../data";
import { useAdminHospitalActs } from "../hooks/useAdminHospitalActs";
import type { HospitalActCareType, HospitalActDetail, HospitalActsSummary } from "../types";
import { ExportMenu } from "../components/ExportMenu";
import { exportCsv } from "@/shared/lib/exportCsv";

const PROVIDER_TYPE_LABELS: Record<string, string> = {
  hopital: "Hôpital",
  clinique: "Clinique",
  pharmacie: "Pharmacie",
  laboratoire: "Laboratoire",
  cabinet: "Cabinet médical",
};

function providerTypeLabel(value?: string): string {
  if (!value) return "—";
  const key = value.toLowerCase().trim();
  return PROVIDER_TYPE_LABELS[key] ?? value;
}

function mapCareType(value: string): HospitalActCareType {
  if (value === "nursing" || value === "consultation" || value === "surgery" || value === "imaging" || value === "laboratory" || value === "imagingLab") {
    return value;
  }
  if (value === "pharmacy") return "consultation";
  return "consultation";
}

export function HospitalActsPage() {
  const { t } = useTranslation();
  const { showToast } = usePageActions();
  const labels = t.safecheck.acts.hospitalActs;
  const isAuditor = getStoredAccountType() === "auditor";
  const [actsFilter, setActsFilter] = useState<"all" | "with" | "without">("all");
  const { summaries, loading, error, usingApi, reload } = useAdminHospitalActs({
    hasActs: actsFilter === "all" ? null : actsFilter === "with",
  });
  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [selectedHospital, setSelectedHospital] = useState<HospitalActsSummary | null>(null);
  const [modalActs, setModalActs] = useState<HospitalActDetail[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [classifying, setClassifying] = useState(false);

  useEffect(() => {
    if (!selectedHospital) {
      setModalActs([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      setModalLoading(true);
      try {
        if (!getAuthToken()) {
          if (!cancelled) setModalActs(getHospitalActDetails(selectedHospital.id));
          return;
        }
        const api = isAuditor
          ? await fetchAuditorHospitalActs(Number(selectedHospital.id))
          : await fetchAdminHospitalActs(Number(selectedHospital.id));
        if (cancelled) return;
        setModalActs(
          (api.medical_acts ?? []).map((act) => ({
            id: String(act.id),
            hospitalId: String(act.hospital_id),
            code: act.code,
            label: act.label,
            careType: mapCareType(act.care_type),
            costUsd: `${Number(act.cost ?? 0).toFixed(2)} USD`,
            delayDays: act.delay_days ?? 0,
          })),
        );
      } catch {
        if (!cancelled) setModalActs(getHospitalActDetails(selectedHospital.id));
      } finally {
        if (!cancelled) setModalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedHospital, isAuditor]);

  const filtered = useMemo(() => {
    return summaries.filter((item) => {
      const haystack = item.hospital.toLowerCase();
      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      return byTopSearch && byTableSearch;
    });
  }, [summaries, topSearch, tableSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = filtered.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = filtered.slice(start, start + pageSize);
  const from = filtered.length === 0 ? 0 : start + 1;
  const to = Math.min(start + pageSize, filtered.length);

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setActsFilter("all");
    setPage(1);
    void reload();
  };

  const runClassifyAll = async (force: boolean) => {
    if (!getAuthToken() || classifying) return;
    setClassifying(true);
    try {
      const result = await classifyAllAdminHospitalActs({ force, limit: 45 });
      const partial = result.truncated ? " (lot partiel — relancez pour continuer)" : "";
      showToast(
        `${result.message ?? "Classification terminée."} ${result.classified} classé(s) IA, ${result.skipped ?? 0} ignoré(s), ${result.hospitals} hôpital(aux).${partial}`,
      );
      await reload();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Classification impossible.");
    } finally {
      setClassifying(false);
    }
  };

  const runClassifyHospital = async (hospitalId: number, force = false) => {
    if (!getAuthToken() || classifying) return;
    setClassifying(true);
    try {
      const result = await classifyAdminHospitalActs(hospitalId, { force, limit: 45 });
      const partial = result.truncated ? " (lot partiel — relancez pour continuer)" : "";
      showToast(
        `${result.message ?? "Classification terminée."} ${result.classified} classé(s) IA, ${result.skipped} ignoré(s).${partial}`,
      );
      await reload();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Classification impossible.");
    } finally {
      setClassifying(false);
    }
  };

  const handleExportCsv = () => {
    exportCsv(
      "actes-par-hopital.csv",
      [labels.columns.hospital, labels.columns.providerType ?? "Type", labels.columns.actCount],
      filtered.map((item) => [item.hospital, providerTypeLabel(item.providerType), item.actCount]),
    );
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.acts },
          { label: labels.title },
        ]}
      />

      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
        {(usingApi || error || loading) && (
          <p className={`mt-1 text-xs ${error ? "text-pink" : "text-muted"}`}>
            {error ?? (loading ? "Chargement…" : `${summaries.length} hôpital(aux) API`)}
          </p>
        )}
      </div>

      {!isAuditor && (
        <div className="mb-4 flex flex-wrap gap-2">
          <Link
            to="/dashboard/acts/category-validation"
            className="inline-flex items-center gap-2 rounded-lg bg-yellow px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Check size={15} />
            {labels.actions.validateCategories}
          </Link>
          <button
            type="button"
            disabled={classifying}
            onClick={() => void runClassifyAll(false)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            title="Classification Gemini (GEMINI_ENABLED + GEMINI_API_KEY requis)"
          >
            {classifying ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
            {labels.actions.aiAllGrids}
          </button>
          <button
            type="button"
            disabled={classifying}
            onClick={() => void runClassifyAll(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60"
            title="Retraite aussi les catégories validées manuellement"
          >
            {classifying ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {labels.actions.reclassifyAi}
          </button>
        </div>
      )}

      <div className="mb-5 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary-soft px-4 py-3">
        <Info size={18} className="mt-0.5 shrink-0 text-primary" />
        <p className="text-sm leading-relaxed text-foreground">
          {isAuditor
            ? "Consultez les grilles tarifaires (code, libellé, prix) de chaque hôpital — lecture seule."
            : `${labels.infoBanner} Classification : IA (Gemini) ou validation manuelle des catégories.`}
        </p>
      </div>

      <div className="theme-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} w-full max-w-sm`}
          />
          <div className="flex items-center gap-2">
            <ExportMenu onExport={handleExportCsv} />
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted">
              <span>{t.safecheck.show}</span>
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                className={selectClass}
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>{t.safecheck.entries}</span>
            </div>
            <select
              value={actsFilter}
              onChange={(event) => {
                setActsFilter(event.target.value as "all" | "with" | "without");
                setPage(1);
              }}
              className={selectClass}
              aria-label={labels.filters.actsLabel}
            >
              <option value="all">{labels.filters.all}</option>
              <option value="with">{labels.filters.withActs}</option>
              <option value="without">{labels.filters.withoutActs}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.columns.hospital}
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.columns.actCount}
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.columns.actions}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageData.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                  <td className="px-4 py-3 text-sm font-medium text-foreground">{item.hospital}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {providerTypeLabel(item.providerType)}
                  </td>
                  <td className="px-4 py-3">
                    {item.actCount > 0 ? (
                      <StatusBadge variant="success">
                        {labels.actsCount.replace("{count}", String(item.actCount))}
                      </StatusBadge>
                    ) : (
                      <span className="text-sm text-muted">{labels.noActsConfigured}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {item.actCount > 0 ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedHospital(item)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary-soft px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-accent/20 hover:border-accent"
                        >
                          <List size={13} />
                          {labels.view}
                        </button>
                        {!isAuditor && (
                          <button
                            type="button"
                            disabled={classifying}
                            onClick={() => void runClassifyHospital(Number(item.id), false)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60"
                            title="Classifier cette grille (IA Gemini)"
                          >
                            <Bot size={14} />
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
          <p className="text-sm text-muted">
            {labels.pagination
              .replace("{from}", String(from))
              .replace("{to}", String(to))
              .replace("{total}", String(filtered.length))}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={currentPage === 1}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.previous}
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                className={`rounded-lg border px-3 py-1.5 text-sm ${
                  pageNumber === currentPage
                    ? "border-primary bg-primary text-white"
                    : "border-border text-muted-foreground hover:bg-accent/20 hover:border-accent"
                }`}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={currentPage === totalPages}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.next}
            </button>
          </div>
        </div>
      </div>

      {selectedHospital && (
        <HospitalActsModal
          hospitalName={
            modalLoading
              ? `${selectedHospital.hospital} (chargement…)`
              : selectedHospital.hospital
          }
          acts={modalActs}
          onClose={() => setSelectedHospital(null)}
        />
      )}
    </>
  );
}
